// src/components/NavBar.jsx
// BAMBOO OPERATIONS 2026 — PROFESSIONAL NAVIGATION — PERMISSION-AWARE BRANCH SWITCHING

import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./NavBar.css";
import bambooLogo from "../assets/bamboo-logo.jpg";
import { useAuth } from "../context/AuthContext";
import PermGate from "./PermGate";
import PrivateMediaManager from "./PrivateMediaManager";
import InstagramFollowAudit from "./InstagramFollowAudit";

import {
  API,
  setApiBase,
  setBranch,
  getActiveBranch,
  unlockBranch,
} from "../apiBase";


import BatchManager from "../BatchManager";
import BulkDeleteModal from "./BulkDeleteModal";
import UserAdminModal from "./UserAdminModal";

function getCookie(name) {
  try {
    const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return m ? decodeURIComponent(m[2]) : "";
  } catch {
    return "";
  }
}

const BRANCH_LABEL = {
  jounieh: "Jounieh",
  tripoli: "Tripoli",
  batroun: "Batroun",
  zgharta: "Zgharta",
  ehden: "Ehden",
};

function safeLower(v) {
  return String(v || "").trim().toLowerCase();
}

export default function NavBar({
  onCreateNewTable,
  onOpenTables,
  onMergeAll,
  onEndOfDay,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    token,
    user,
    isAuthenticated,
    logout,
    activeBranch,
    availableBranches = [],
    switchBranch,
  } = useAuth();

  const BASE = (API || "").replace(/\/+$/, "");
  const userEmail = safeLower(user?.email || user?.username || "");

  const allowedBranches = useMemo(() => {
    const all = Object.keys(BRANCH_LABEL);

    const normalizeList = (raw) => {
      if (Array.isArray(raw)) return raw;
      if (raw == null) return [];
      return String(raw)
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
    };

    const preferred = normalizeList(availableBranches);
    const fallback = normalizeList(user?.branches || user?.br || user?.branch || user?.city || []);
    const source = preferred.length ? preferred : fallback;
    const cleaned = source.map(safeLower).filter(Boolean);

    if (cleaned.includes("*")) return all;
    return all.filter((branch) => cleaned.includes(branch));
  }, [availableBranches, user?.branches, user?.br, user?.branch, user?.city]);

  const [liveBranch, setLiveBranchState] = useState(() =>
    safeLower(activeBranch || getActiveBranch() || "")
  );

  const targetBranch = useMemo(() => {
    const requested = safeLower(activeBranch || liveBranch || getActiveBranch() || "");
    if (requested && allowedBranches.includes(requested)) return requested;
    return allowedBranches[0] || requested || "";
  }, [activeBranch, liveBranch, allowedBranches]);

  const branchReady = Boolean(targetBranch);

  useEffect(() => {
    const sync = (event) => {
      const eventBranch = safeLower(event?.detail?.branch || "");
      const next = eventBranch || safeLower(getActiveBranch() || activeBranch || "");
      if (next) setLiveBranchState(next);
    };

    sync();
    window.addEventListener("bamboo:branch", sync);
    window.addEventListener("app:branch", sync);

    return () => {
      window.removeEventListener("bamboo:branch", sync);
      window.removeEventListener("app:branch", sync);
    };
  }, [activeBranch]);

  // Clear any legacy e-mail branch lock from older navbar versions. The user can
  // now switch only among branches granted by AuthContext/auth_routes.py.
  useEffect(() => {
    if (!isAuthenticated || !targetBranch) return;

    const contextBranch = safeLower(activeBranch || "");
    if (contextBranch !== targetBranch && typeof switchBranch === "function") {
      Promise.resolve(switchBranch(targetBranch)).catch((err) => {
        console.error("Initial branch sync failed:", err);
      });
    }

    try {
      unlockBranch({ reload: false });
    } catch {}

    try {
      setBranch(targetBranch, { reload: false, force: true });
    } catch {}

    setLiveBranchState(targetBranch);

    try {
      window.dispatchEvent(
        new CustomEvent("bamboo:branchReady", {
          detail: { branch: targetBranch, email: userEmail },
        })
      );
    } catch {}
  }, [isAuthenticated, targetBranch, userEmail, activeBranch, switchBranch]);

  const getAuthHeaders = () => {
    const h = { "Content-Type": "application/json" };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  };

  const waitBranchReady = useCallback(async () => {
    if (!targetBranch) return;

    const current = safeLower(getActiveBranch() || activeBranch || "");
    if (current !== targetBranch) {
      try {
        unlockBranch({ reload: false });
      } catch {}
      try {
        setBranch(targetBranch, { reload: false, force: true });
      } catch {}
      setLiveBranchState(targetBranch);
    }
  }, [activeBranch, targetBranch]);

  const apiFetch = useCallback(
    async (path, init = {}) => {
      const url = `${BASE}${path}`;
      const csrf = getCookie("csrf_token") || getCookie("csrf") || "";

      const method = String(init.method || (init.body ? "POST" : "GET")).toUpperCase();
      const headers = { ...(init.headers || {}) };

      if (init.body && !(init.body instanceof FormData)) {
        headers["Content-Type"] = headers["Content-Type"] || "application/json";
      }

      if (["POST", "PUT", "PATCH", "DELETE"].includes(method) && csrf) {
        headers["x-csrf-token"] = headers["x-csrf-token"] || csrf;
      }

      const b = safeLower(getActiveBranch() || targetBranch);
      if (b) {
        if (!headers["x-branch"]) headers["x-branch"] = b;
        if (!headers["X-Branch"]) headers["X-Branch"] = b;
      }

      const res = await fetch(url, {
        mode: "cors",
        credentials: "include",
        ...init,
        method,
        headers,
      });

      const text = await res.text().catch(() => "");
      let data = null;

      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }

      if (!res.ok) {
        const detail = data?.detail || data?.message || text || "Request failed";
        throw new Error(`${res.status} ${res.statusText} — ${detail}`);
      }

      return data || {};
    },
    [BASE, targetBranch]
  );

  const apiFetchWaitBranch = useCallback(
    async (path, init = {}) => {
      await waitBranchReady();
      return apiFetch(path, init);
    },
    [waitBranchReady, apiFetch]
  );

  const mergeBusyRef = useRef(false);
  const mergeAbortRef = useRef(null);
  const [mergeBusy, setMergeBusy] = useState(false);
  const [forceEodBusy, setForceEodBusy] = useState(false);
  const [forceGlobalMergeBusy, setForceGlobalMergeBusy] = useState(false);

  const doMergeAll = useCallback(async () => {
    if (mergeBusyRef.current) return;

    mergeBusyRef.current = true;
    setMergeBusy(true);

    try {
      if (mergeAbortRef.current) mergeAbortRef.current.abort();
    } catch {}

    const ac = new AbortController();
    mergeAbortRef.current = ac;

    try {
      let data = null;

      try {
        data = await apiFetchWaitBranch("/merge/tables?archive=0", {
          method: "POST",
          signal: ac.signal,
        });
      } catch (e1) {
        const msg1 = String(e1?.message || e1 || "");
        if (/404|405|not found|method not allowed/i.test(msg1)) {
          data = await apiFetchWaitBranch("/merge-tables", {
            method: "POST",
            signal: ac.signal,
          });
        } else {
          throw e1;
        }
      }

      const skipped =
        Array.isArray(data?.skipped) && data.skipped.length
          ? `\n\nSkipped:\n- ${data.skipped.join("\n- ")}`
          : "";

      alert(`${data?.message || "Merged."}${skipped}`);
    } catch (err) {
      const msg = String(err?.message || err || "");
      if (!/aborted|abort/i.test(msg)) {
        alert("❌ Merge failed:\n" + msg);
      }
    } finally {
      mergeBusyRef.current = false;
      setMergeBusy(false);
    }
  }, [apiFetchWaitBranch]);

  useEffect(() => {
    return () => {
      try {
        if (mergeAbortRef.current) mergeAbortRef.current.abort();
      } catch {}

      try {
        if (exportTimerRef.current) window.clearInterval(exportTimerRef.current);
      } catch {}
    };
  }, []);

  const [apiHealth, setApiHealth] = useState(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        await apiFetchWaitBranch("/health", { method: "GET" });
        if (alive) setApiHealth(true);
      } catch {
        if (alive) setApiHealth(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [apiFetchWaitBranch, liveBranch]);

  const applyApiChoice = (value) => {
    if (!value) return;

    const u = new URL(window.location.href);

    if (value === "reset") {
      u.searchParams.delete("apiBase");
      setApiBase("reset");
    } else {
      u.searchParams.set("apiBase", value);
      setApiBase(value);
    }

    u.searchParams.delete("city");
    window.location.href = u.toString();
  };

  const onPickApi = (e) => applyApiChoice(e.target.value);

  const currentApiLabel = useMemo(() => {
    if (/^https:\/\/api\.bamboome\.me\/?$/i.test(BASE)) return "global";
    if (/^http:\/\/127\.0\.0\.1:8000\/?$/i.test(BASE)) return "local";

    try {
      const u = new URL(BASE);
      return u.hostname;
    } catch {
      return BASE;
    }
  }, [BASE]);

  const activeBranchLabel = useMemo(() => {
    const b = safeLower(activeBranch || getActiveBranch() || targetBranch);
    return BRANCH_LABEL[b] || b || "No branch";
  }, [activeBranch, liveBranch, targetBranch]);

  const [openMenu, setOpenMenu] = useState(null);
  const actionsRef = useRef(null);
  const navRef = useRef(null);
  const endRef = useRef(null);
  const optRef = useRef(null);
  const ckRef = useRef(null);

  const [fileDlg, setFileDlg] = useState(false);
  const [openingFolder, setOpeningFolder] = useState(false);

  const [batchOpen, setBatchOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [showUsersDlg, setShowUsersDlg] = useState(false);
  const [showPrivateMediaDlg, setShowPrivateMediaDlg] = useState(false);
  const [instagramAuditOpen, setInstagramAuditOpen] = useState(false);

  const exportTimerRef = useRef(null);
  const vcfOpenInputRef = useRef(null);
  const [vcfOpenBusy, setVcfOpenBusy] = useState(false);
  const [vcfExportOpen, setVcfExportOpen] = useState(false);
  const [vcfExportBusy, setVcfExportBusy] = useState(false);
  const [vcfExportPercent, setVcfExportPercent] = useState(0);
  const [vcfExportMessage, setVcfExportMessage] = useState("Preparing export...");
  const [vcfExportStatus, setVcfExportStatus] = useState("idle");

  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);

  // Cross-branch customer visit history (reads each branch table.db through backend).
  const [visitSearchOpen, setVisitSearchOpen] = useState(false);
  const [visitQuery, setVisitQuery] = useState("");
  const [visitLoading, setVisitLoading] = useState(false);
  const [visitError, setVisitError] = useState("");
  const [visitData, setVisitData] = useState(null);

  const toggleMenu = (key) => {
    setMobileMoreOpen(false);
    setOpenMenu((m) => (m === key ? null : key));
  };

  const closeAll = useCallback(() => {
    setOpenMenu(null);
    setMobileMoreOpen(false);
  }, []);

  const runForcedEndOfDay = useCallback(async () => {
    if (forceEodBusy) return;
    const branchLabel = BRANCH_LABEL[targetBranch] || targetBranch || "selected branch";
    const confirmed = window.confirm(
      `Force End of Day for ${branchLabel} now?\n\n` +
      "This bypasses the normal clock/date schedule, merges table.db into contacts.db, and creates the EOD backups."
    );
    if (!confirmed) return;

    closeAll();
    setForceEodBusy(true);
    try {
      const data = await apiFetchWaitBranch("/end-of-day/force?confirm=FORCE", { method: "POST" });
      alert(
        `✅ ${data?.message || "Forced End of Day completed."}\n\n` +
        `Inserted: ${Number(data?.inserted || 0)}\n` +
        `Replaced duplicates: ${Number(data?.removed || 0)}\n` +
        `Skipped invalid rows: ${Number(data?.skipped || 0)}`
      );
    } catch (error) {
      alert(`❌ Forced End of Day failed:\n${error?.message || String(error)}`);
    } finally {
      setForceEodBusy(false);
    }
  }, [apiFetchWaitBranch, closeAll, forceEodBusy, targetBranch]);

  const runForcedGlobalTableMerge = useCallback(async () => {
    if (forceGlobalMergeBusy) return;
    const confirmed = window.confirm(
      "Force merge every branch table.db into contacts.db now?\n\n" +
      "A backup is created first. Contacts are deduplicated by phone and every visit is preserved in customer visit history."
    );
    if (!confirmed) return;

    closeAll();
    setForceGlobalMergeBusy(true);
    try {
      const data = await apiFetchWaitBranch(
        "/force-merge-table-dbs-to-contacts?confirm=FORCE",
        { method: "POST" }
      );
      const warnings = Array.isArray(data?.warnings) ? data.warnings : [];
      alert(
        `✅ ${data?.message || "Forced global merge completed."}\n\n` +
        `Branch table.db files: ${Number(data?.sources_scanned || 0)}\n` +
        `Source rows: ${Number(data?.source_rows || 0)}\n` +
        `New contacts: ${Number(data?.contacts_inserted || 0)}\n` +
        `Improved contacts: ${Number(data?.contacts_updated || 0)}\n` +
        `Duplicates removed: ${Number(data?.duplicates_removed || 0)}\n` +
        `New visit-history rows: ${Number(data?.visits_added || 0)}` +
        (warnings.length ? `\n\nWarnings:\n- ${warnings.slice(0, 12).join("\n- ")}` : "")
      );
    } catch (error) {
      alert(`❌ Forced global table merge failed:\n${error?.message || String(error)}`);
    } finally {
      setForceGlobalMergeBusy(false);
    }
  }, [apiFetchWaitBranch, closeAll, forceGlobalMergeBusy]);


  const handleBranchChange = useCallback(
    async (rawBranch) => {
      const next = safeLower(rawBranch);
      if (!next || !allowedBranches.includes(next)) {
        alert("This branch is not assigned to your account.");
        return;
      }

      if (next === safeLower(activeBranch || liveBranch || getActiveBranch() || "")) {
        closeAll();
        return;
      }

      try {
        try {
          unlockBranch({ reload: false });
        } catch {}

        if (typeof switchBranch === "function") {
          await Promise.resolve(switchBranch(next));
        }

        // Keep legacy apiBase consumers synchronized with AuthContext.
        try {
          setBranch(next, { reload: false, force: true });
        } catch {}

        setLiveBranchState(next);

        try {
          window.dispatchEvent(new CustomEvent("bamboo:branch", { detail: { branch: next } }));
          window.dispatchEvent(new CustomEvent("app:branch", { detail: { branch: next } }));
        } catch {}

        closeAll();
      } catch (err) {
        console.error("Branch switch failed:", err);
        alert(err?.message || "Could not switch branch.");
      }
    },
    [allowedBranches, activeBranch, liveBranch, switchBranch, closeAll]
  );

  useEffect(() => {
    const onDocPointerDown = (e) => {
      if (
        openMenu &&
        !actionsRef.current?.contains(e.target) &&
        !navRef.current?.contains(e.target) &&
        !endRef.current?.contains(e.target) &&
        !optRef.current?.contains(e.target) &&
        !ckRef.current?.contains(e.target)
      ) {
        setOpenMenu(null);
      }
    };

    document.addEventListener("pointerdown", onDocPointerDown, { passive: true });
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [openMenu]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeAll();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeAll]);

  const normalizedRoles = [
    ...(Array.isArray(user?.roles)
      ? user.roles
      : String(user?.roles || "").split(",")),
    user?.role,
  ].map(safeLower).filter(Boolean);
  const isAdmin = normalizedRoles.some((role) =>
    ["admin", "owner", "superadmin", "super_admin"].includes(role)
  );

  const isAdminAuthed = Boolean(isAuthenticated && isAdmin);

  const handleLogout = async () => {
    try {
      await apiFetchWaitBranch("/auth/logout", { method: "POST" }).catch(() => {});
    } finally {
      try {
        unlockBranch({ reload: false });
      } catch {}

      try {
        setBranch("", { reload: false, force: true });
      } catch {}

      logout();
      closeAll();
      navigate("/login");
    }
  };

  const openBackendFolderOnServer = async () => {
    setOpeningFolder(true);
    try {
      await apiFetchWaitBranch("/backend/open-folder", { method: "POST" });
      alert("✅ Folder opened on the SERVER machine.");
    } catch (e) {
      alert("❌ Could not open folder:\n" + (e?.message || String(e)));
    } finally {
      setOpeningFolder(false);
    }
  };

  const go = (path) => {
    closeAll();
    navigate(path);
  };

  const openCustomerVisitSearch = () => {
    closeAll();
    setVisitError("");
    setVisitData(null);
    setVisitSearchOpen(true);
  };

  const closeCustomerVisitSearch = () => {
    if (visitLoading) return;
    setVisitSearchOpen(false);
    setVisitError("");
  };

  const runCustomerVisitSearch = async (event) => {
    event?.preventDefault?.();
    const q = String(visitQuery || "").trim();

    if (q.length < 2) {
      setVisitError("Enter a customer name or phone number.");
      setVisitData(null);
      return;
    }

    setVisitLoading(true);
    setVisitError("");

    try {
      const data = await apiFetchWaitBranch(
        `/customer-visits/search?q=${encodeURIComponent(q)}&limit=150`,
        { method: "GET" }
      );
      setVisitData(data || { items: [] });
    } catch (e) {
      setVisitData(null);
      setVisitError(e?.message || String(e) || "Customer visit search failed.");
    } finally {
      setVisitLoading(false);
    }
  };

  const stopExportTimer = useCallback(() => {
    try {
      if (exportTimerRef.current) {
        window.clearInterval(exportTimerRef.current);
        exportTimerRef.current = null;
      }
    } catch {}
  }, []);

  const resetExportLoading = useCallback(() => {
    stopExportTimer();
    setVcfExportOpen(false);
    setVcfExportBusy(false);
    setVcfExportPercent(0);
    setVcfExportMessage("Preparing export...");
    setVcfExportStatus("idle");
  }, [stopExportTimer]);

  const startExportLoading = useCallback(() => {
    stopExportTimer();

    setVcfExportOpen(true);
    setVcfExportBusy(true);
    setVcfExportStatus("loading");
    setVcfExportPercent(8);
    setVcfExportMessage("Preparing contacts database...");

    exportTimerRef.current = window.setInterval(() => {
      setVcfExportPercent((p) => {
        if (p < 35) return p + 4;
        if (p < 70) return p + 2;
        if (p < 92) return p + 1;
        return p;
      });

      setVcfExportMessage((msg) => {
        if (msg === "Preparing contacts database...") return "Reading contacts...";
        if (msg === "Reading contacts...") return "Building VCF file...";
        if (msg === "Building VCF file...") return "Almost ready...";
        return msg;
      });
    }, 420);
  }, [stopExportTimer]);

  const getDownloadFilename = (response, fallback) => {
    try {
      const cd = response.headers.get("content-disposition") || "";
      const utf8 = cd.match(/filename\*=UTF-8''([^;]+)/i);
      if (utf8?.[1]) return decodeURIComponent(utf8[1].replace(/["]/g, ""));

      const normal = cd.match(/filename="?([^"]+)"?/i);
      if (normal?.[1]) return normal[1].trim();
    } catch {}

    return fallback;
  };

  const openContactsVCFPicker = () => {
    closeAll();

    window.setTimeout(() => {
      try {
        vcfOpenInputRef.current?.click();
      } catch {}
    }, 0);
  };

  const openContactsVCF = async (e) => {
    const file = e?.target?.files?.[0];

    if (!file) return;

    const fileName = String(file.name || "contacts.vcf");
    const looksLikeVcf = /\.vcf$/i.test(fileName) || /vcard/i.test(String(file.type || ""));

    if (!looksLikeVcf) {
      alert("❌ Please choose a valid .vcf file.");
      try {
        e.target.value = "";
      } catch {}
      return;
    }

    setVcfOpenBusy(true);

    try {
      await waitBranchReady();

      const branch = safeLower(getActiveBranch() || targetBranch || "contacts");
      const csrf = getCookie("csrf_token") || getCookie("csrf") || "";

      const formData = new FormData();
      formData.append("file", file, fileName);
      formData.append("branch", branch);

      const headers = {
        "x-branch": branch,
        "X-Branch": branch,
      };

      if (token) headers.Authorization = `Bearer ${token}`;
      if (csrf) headers["x-csrf-token"] = csrf;

      const response = await fetch(`${BASE}/contacts/import-vcf`, {
        method: "POST",
        credentials: "include",
        headers,
        body: formData,
      });

      const text = await response.text().catch(() => "");
      let data = null;

      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }

      if (!response.ok) {
        const detail = data?.detail || data?.message || text || `Import failed (${response.status})`;
        throw new Error(detail);
      }

      const imported = Number(data?.imported ?? data?.created ?? data?.inserted ?? 0);
      const skipped = Number(data?.skipped ?? 0);
      const duplicates = Number(data?.duplicates ?? 0);
      const total = Number(data?.contacts_found ?? data?.total ?? imported + skipped + duplicates);

      alert(
        `✅ VCF opened and imported successfully.\n\nFile: ${fileName}\nBranch: ${branch}\nContacts found: ${total}\nImported: ${imported}\nSkipped: ${skipped}\nDuplicates: ${duplicates}`
      );

      try {
        window.dispatchEvent(
          new CustomEvent("bamboo:contactsImported", {
            detail: { fileName, branch, total, imported, skipped, duplicates },
          })
        );
      } catch {}
    } catch (err) {
      alert("❌ Could not open/import VCF:\n" + (err?.message || String(err)));
    } finally {
      setVcfOpenBusy(false);
      try {
        e.target.value = "";
      } catch {}
    }
  };

  const exportContactsVCF = async () => {
    closeAll();

    if (vcfExportBusy) return;

    startExportLoading();

    try {
      await waitBranchReady();

      const branch = safeLower(getActiveBranch() || targetBranch || "export");
      const headers = {
        "x-branch": branch,
        "X-Branch": branch,
      };

      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${BASE}/contacts/export-vcf`, {
        method: "GET",
        credentials: "include",
        headers,
      });

      if (!response.ok) {
        let detail = "";

        try {
          const data = await response.json();
          detail = data?.detail || data?.message || "";
        } catch {
          detail = await response.text().catch(() => "");
        }

        throw new Error(detail || `Export failed (${response.status})`);
      }

      setVcfExportMessage("Downloading VCF file...");
      setVcfExportPercent(96);

      const blob = await response.blob();

      if (!blob || blob.size === 0) {
        throw new Error("The backend returned an empty VCF file.");
      }

      const fileName = getDownloadFilename(response, `contacts_${branch}.vcf`);
      const exportedContacts = response.headers.get("x-exported-contacts") || "";
      const skippedContacts = response.headers.get("x-skipped-contacts") || "";
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.style.display = "none";

      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);

      stopExportTimer();
      setVcfExportStatus("success");
      setVcfExportPercent(100);
      setVcfExportMessage(
        `Export completed: ${fileName}${
          exportedContacts ? ` — ${exportedContacts} contacts` : ""
        }${skippedContacts ? `, ${skippedContacts} skipped` : ""}`
      );

      window.setTimeout(() => {
        resetExportLoading();
      }, 1100);
    } catch (e) {
      stopExportTimer();
      setVcfExportBusy(false);
      setVcfExportStatus("error");
      setVcfExportPercent(100);
      setVcfExportMessage(e?.message || String(e) || "Could not export contacts.");
    }
  };


  const isHomeRoute = location.pathname === "/" || location.pathname === "/home";

  const goBackSafely = () => {
    closeAll();

    try {
      if (window.history.length > 1) {
        navigate(-1);
        return;
      }
    } catch {}

    navigate("/home");
  };

  useEffect(() => {
    if (!mobileMoreOpen) return;

    document.body.classList.add("bamboo-nav-overlay-open");
    return () => document.body.classList.remove("bamboo-nav-overlay-open");
  }, [mobileMoreOpen]);

  return (
    <header className="nav nav-root" role="banner" style={{ position: "relative", zIndex: 30 }}>
      <input
        ref={vcfOpenInputRef}
        type="file"
        accept=".vcf,text/vcard,text/x-vcard"
        onChange={openContactsVCF}
        style={{ display: "none" }}
        aria-hidden="true"
        tabIndex={-1}
      />

      <div className="topbar">
        <div className="left nav-brand-cluster">
          <Link to="/home" className="brand bamboo-nav-brand" title="Bamboo Home" aria-label="Bamboo Home" onClick={closeAll}>
            <span className="brand-logo-frame">
              <img src={bambooLogo} alt="" aria-hidden="true" />
            </span>
            <span className="brand-copy">
              <strong>Bamboo</strong>
              <small>Operations</small>
            </span>
          </Link>

          {isAuthenticated && (
            <label className="nav-branch-switch" title="Switch active branch">
              <span className="nav-branch-caption">Active branch</span>
              <span className="nav-branch-field">
                <span className="nav-branch-pin">●</span>
                <select
                  value={targetBranch}
                  onChange={(e) => handleBranchChange(e.target.value)}
                  disabled={allowedBranches.length <= 1}
                  aria-label="Switch active branch"
                >
                  {allowedBranches.map((branch) => (
                    <option key={branch} value={branch}>{BRANCH_LABEL[branch] || branch}</option>
                  ))}
                </select>
              </span>
            </label>
          )}

          <div className="nav-quick-controls" aria-label="Quick navigation">
            <button
              type="button"
              className="btn nav-quick-button"
              onClick={goBackSafely}
              aria-label="Back"
              title="Back"
            >
              <span className="nav-button-icon">←</span>
              <span className="nav-quick-label">Back</span>
            </button>

            <button
              type="button"
              className={`btn nav-quick-button ${isHomeRoute ? "is-active" : ""}`}
              onClick={() => go("/home")}
              aria-label="Home"
              aria-current={isHomeRoute ? "page" : undefined}
              title="Home"
            >
              <span className="nav-button-icon">⌂</span>
              <span className="nav-quick-label">Home</span>
            </button>
          </div>
        </div>

        <div className="right groupbar" style={{ flexWrap: "wrap" }}>
          <div className="group" ref={actionsRef}>
            <button className="btn bigtap" onClick={() => toggleMenu("actions")} title="Actions">
              <span className="nav-button-icon">＋</span><span className="hide-sm">Actions</span>
            </button>

            {openMenu === "actions" && (
              <div className="popover" role="menu" aria-label="Actions">
                {typeof onCreateNewTable === "function" && (
                  <PermGate perm="create_table">
                    <button
                      className="pop-item"
                      onClick={() => {
                        closeAll();
                        onCreateNewTable();
                      }}
                    >
                      ➕ New Table
                    </button>
                  </PermGate>
                )}

                {typeof onOpenTables === "function" && (
                  <PermGate perm="open_saved_tables">
                    <button
                      className="pop-item"
                      onClick={() => {
                        closeAll();
                        onOpenTables();
                      }}
                    >
                      📂 Saved Tables
                    </button>
                  </PermGate>
                )}

                <PermGate perm="merge_all">
                  <button
                    className="pop-item"
                    disabled={mergeBusy}
                    onClick={() => {
                      if (mergeBusy) return;
                      closeAll();
                      if (onMergeAll) onMergeAll();
                      else doMergeAll();
                    }}
                    title={mergeBusy ? "Merging..." : "Merge All"}
                  >
                    {mergeBusy ? "⏳ Merging..." : "🔁 Merge All"}
                  </button>
                </PermGate>
              </div>
            )}
          </div>

          <div className="group" ref={navRef}>
            <button className="btn bigtap" onClick={() => toggleMenu("nav")} title="Navigate">
              <span className="nav-button-icon">🧭</span><span className="hide-sm">Navigate</span>
            </button>

            {openMenu === "nav" && (
              <div className="popover" role="menu" aria-label="Navigate">
                <Link to="/" className="pop-link" onClick={closeAll}>🏠 Home</Link>
                <Link to="/search-date" className="pop-link" onClick={closeAll}>📅 Search by Date</Link>
                <Link to="/birthdays" className="pop-link" onClick={closeAll}>🎂 Birthday Search</Link>
                <Link to="/reservations" className="pop-link" onClick={closeAll}>📆 Reservations</Link>
                <Link to="/guest-search" className="pop-link" onClick={closeAll}>🔍 Search Guest</Link>
                <PermGate perm="nav_control"><Link to="/table-db" className="pop-link" onClick={closeAll}>🧾 Table Database</Link></PermGate>
                <button
                  className="pop-item"
                  onClick={openCustomerVisitSearch}
                  title="Search all branch table.db files by customer name or phone"
                >
                  🧾 Customer Visit History
                </button>
                <Link to="/control" className="pop-link" onClick={closeAll}>⚙ Control Panel</Link>
                <Link to="/competitions" className="pop-link" onClick={closeAll}>🏆 Competitions</Link>
                {isAuthenticated && <button className="pop-item" onClick={() => { closeAll(); setInstagramAuditOpen(true); }}>📸 Instagram Follow Audit</button>}
              </div>
            )}
          </div>

          {typeof onEndOfDay === "function" && (
            <div className="group" ref={endRef}>
              <button className="btn bigtap" onClick={() => toggleMenu("end")} title="End">
                <span className="nav-button-icon">🌙</span><span className="hide-sm">End</span>
              </button>

              {openMenu === "end" && (
                <div className="popover" role="menu" aria-label="End">
                  <PermGate perm="end_of_day">
                    <button
                      className="pop-item danger"
                      onClick={() => {
                        closeAll();
                        onEndOfDay();
                      }}
                    >
                      🌙 End of Day
                    </button>
                  </PermGate>
                </div>
              )}
            </div>
          )}

          {isAdminAuthed && (
            <>
              <div className="group" ref={optRef}>
                <button className="btn bigtap" onClick={() => toggleMenu("options")} title="Options">
                  <span className="nav-button-icon">🛡️</span><span className="hide-sm">Options</span>
                </button>

                {openMenu === "options" && (
                  <div className="popover" role="menu" aria-label="Options">
                    <button
                      className="pop-item danger"
                      onClick={runForcedEndOfDay}
                      disabled={forceEodBusy || forceGlobalMergeBusy}
                      title="Run End of Day immediately for the selected branch"
                    >
                      {forceEodBusy ? "⏳ Forcing End of Day..." : "🌙 Force End of Day"}
                    </button>

                    <button
                      className="pop-item danger"
                      onClick={runForcedGlobalTableMerge}
                      disabled={forceGlobalMergeBusy || forceEodBusy}
                      title="Merge every branch table.db into contacts.db without duplicate phone records"
                    >
                      {forceGlobalMergeBusy ? "⏳ Merging All Branches..." : "🔁 Force table.db → contacts.db"}
                    </button>

                    <button
                      className="pop-item"
                      onClick={openContactsVCFPicker}
                      disabled={vcfOpenBusy}
                      title="Open/import a .vcf file from Android, iPhone, or desktop into contacts.db"
                    >
                      {vcfOpenBusy ? "⏳ Opening VCF..." : "📂 Open VCF"}
                    </button>

                    <button className="pop-item" onClick={exportContactsVCF} disabled={vcfExportBusy}>
                      {vcfExportBusy ? "⏳ Exporting VCF..." : "📇 Export Contacts VCF"}
                    </button>

                    <button
                      className="pop-item"
                      onClick={() => go("/chat-seen")}
                      title="Upload WhatsApp chat export, detect phone numbers, match contacts.db, and save chat dates"
                    >
                      👁 Chat Seen Contacts
                    </button>

                    <button
                      className="pop-item"
                      onClick={() => {
                        closeAll();
                        setBulkDeleteOpen(true);
                      }}
                    >
                      🗑 Bulk Delete
                    </button>

                    <button
                      className="pop-item"
                      onClick={() => {
                        closeAll();
                        setFileDlg(true);
                      }}
                    >
                      📁 Backend Folder
                    </button>

                    <button
                      className="pop-item"
                      onClick={() => {
                        closeAll();
                        setShowPrivateMediaDlg(true);
                      }}
                    >
                      🖼 Private Media
                    </button>

                    <button
                      className="pop-item"
                      onClick={() => {
                        closeAll();
                        setShowUsersDlg(true);
                      }}
                    >
                      👥 Users
                    </button>
                  </div>
                )}
              </div>

              <div className="group" ref={ckRef}>
                <button className="btn bigtap" onClick={() => toggleMenu("ck")} title="Central Kitchen">
                  <span className="nav-button-icon">👨‍🍳</span><span className="hide-sm">Central&nbsp;Kitchen</span>
                </button>

                {openMenu === "ck" && (
                  <div className="popover" role="menu" aria-label="Central Kitchen">
                    <button
                      className="pop-item"
                      onClick={() => {
                        closeAll();
                        setBatchOpen(true);
                      }}
                    >
                      🧪 CK Batch / Recipe Manager
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          <div
            className="group"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <span
              role="button"
              onClick={() => window.open(`${BASE}/health`, "_blank")}
              title={`API: ${BASE}\nBranch: ${targetBranch || "(none)"}\nUser: ${userEmail || "(none)"}\nBranch Ready: ${branchReady ? "yes" : "no"}\nClick to open /health`}
              style={{
                padding: "4px 8px",
                borderRadius: 999,
                background:
                  apiHealth === true
                    ? "rgba(0,200,0,.15)"
                    : apiHealth === false
                    ? "rgba(200,0,0,.15)"
                    : "var(--line, rgba(255,255,255,.08))",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              {apiHealth === true ? "✅ API OK" : apiHealth === false ? "⚠ API OFF" : "… API"}
            </span>

            <div className="hide-sm nav-active-branch-badge" title="Current branch context">
              <span className="nav-active-dot" />
              {activeBranchLabel}
            </div>

            <label className="hide-sm" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
              <span style={{ opacity: 0.8 }}>API:</span>
              <select onChange={onPickApi} value="" title={`Current: ${currentApiLabel} → ${BASE}`}>
                <option value="" disabled>{currentApiLabel}</option>
                <option value="https://api.bamboome.me">global</option>
                <option value="http://127.0.0.1:8000">local</option>
                <option value="reset">auto (reset)</option>
              </select>
            </label>

            {isAuthenticated ? (
              <div className="user-chip" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span
                  title="Signed in user"
                  style={{
                    padding: "4px 8px",
                    borderRadius: 999,
                    background: "var(--line, rgba(255,255,255,.08))",
                    fontSize: 13,
                  }}
                >
                  👤{" "}
                  {user?.first_name || user?.last_name
                    ? `${user?.first_name || ""} ${user?.last_name || ""}`.trim()
                    : user?.name || user?.username || user?.sub || "User"}
                </span>

                <button className="btn" title="Log out" onClick={handleLogout} aria-label="Log out">
                  🚪 Log out
                </button>
              </div>
            ) : (
              <button className="btn" title="Sign in" onClick={() => navigate("/login")} aria-label="Sign in">
                🔐 Sign in
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bottombar" aria-label="Mobile navigation">
        <button
          className="btn bigtap bbtn mobile-nav-secondary"
          onClick={goBackSafely}
          aria-label="Back"
          title="Back"
        >
          <span className="mobile-nav-symbol">←</span><span className="lbl">Back</span>
        </button>

        <button
          className={`btn bigtap bbtn mobile-nav-secondary ${isHomeRoute ? "is-active" : ""}`}
          onClick={() => go("/home")}
          aria-label="Home"
          aria-current={isHomeRoute ? "page" : undefined}
          title="Home"
        >
          <span className="mobile-nav-symbol">🏠</span><span className="lbl">Home</span>
        </button>

        {isAuthenticated && allowedBranches.length > 1 && (
          <button
            className="btn bigtap bbtn mobile-nav-secondary"
            onClick={() => {
              setOpenMenu(null);
              setMobileMoreOpen(true);
            }}
            aria-label={`Active branch ${activeBranchLabel}`}
            title="Switch branch"
          >
            <span className="mobile-nav-symbol">📍</span><span className="lbl">{activeBranchLabel}</span>
          </button>
        )}

        <button
          className="btn bigtap bbtn mobile-more-button"
          onClick={() => {
            setOpenMenu(null);
            setMobileMoreOpen((v) => !v);
          }}
          aria-label="More"
          title="More"
        >
          <span className="mobile-nav-symbol">✨</span><span className="lbl">More</span>
        </button>
      </div>

      {mobileMoreOpen && (
        <div className="sheet" role="dialog" aria-modal="true" aria-label="More menu">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <div style={{ fontWeight: 800 }}>More</div>
            <button className="btn tiny" style={{ marginLeft: "auto" }} onClick={() => setMobileMoreOpen(false)}>
              Close
            </button>
          </div>

          {isAuthenticated && (
            <div className="mobile-branch-panel">
              <div className="mobile-branch-label">Active branch</div>
              <select
                value={targetBranch}
                onChange={(e) => handleBranchChange(e.target.value)}
                disabled={allowedBranches.length <= 1}
                aria-label="Switch active branch"
              >
                {allowedBranches.map((branch) => (
                  <option key={`mobile-${branch}`} value={branch}>{BRANCH_LABEL[branch] || branch}</option>
                ))}
              </select>
              <small>You can switch only between branches assigned to your user account.</small>
            </div>
          )}

          <div className="api-row" style={{ marginBottom: 10 }}>
            <div className="api-current">
              {apiHealth === true ? "✅" : apiHealth === false ? "⚠" : "…"} {currentApiLabel} {" · "} 📍 {activeBranchLabel}
            </div>
            <select className="api-select" onChange={onPickApi} value="" title={`Current: ${currentApiLabel} → ${BASE}`}>
              <option value="" disabled>{currentApiLabel}</option>
              <option value="https://api.bamboome.me">global</option>
              <option value="http://127.0.0.1:8000">local</option>
              <option value="reset">auto (reset)</option>
            </select>
          </div>

          <div className="sheet-sec">
            <div className="sheet-title">Actions</div>

            {typeof onCreateNewTable === "function" && (
              <PermGate perm="create_table">
                <button
                  className="sheet-item"
                  onClick={() => {
                    closeAll();
                    onCreateNewTable();
                  }}
                >
                  ➕ New Table
                </button>
              </PermGate>
            )}

            {typeof onOpenTables === "function" && (
              <PermGate perm="open_saved_tables">
                <button
                  className="sheet-item"
                  onClick={() => {
                    closeAll();
                    onOpenTables();
                  }}
                >
                  📂 Saved Tables
                </button>
              </PermGate>
            )}

            <PermGate perm="merge_all">
              <button
                className="sheet-item"
                disabled={mergeBusy}
                onClick={() => {
                  if (mergeBusy) return;
                  closeAll();
                  if (onMergeAll) onMergeAll();
                  else doMergeAll();
                }}
                title={mergeBusy ? "Merging..." : "Merge All"}
              >
                {mergeBusy ? "⏳ Merging..." : "🔁 Merge All"}
              </button>
            </PermGate>

            {isAdminAuthed && (
              <>
                <button
                  className="sheet-item danger"
                  onClick={runForcedEndOfDay}
                  disabled={forceEodBusy || forceGlobalMergeBusy}
                >
                  {forceEodBusy ? "⏳ Forcing End of Day..." : "🌙 Force End of Day"}
                </button>

                <button
                  className="sheet-item danger"
                  onClick={runForcedGlobalTableMerge}
                  disabled={forceGlobalMergeBusy || forceEodBusy}
                >
                  {forceGlobalMergeBusy ? "⏳ Merging All Branches..." : "🔁 Force table.db → contacts.db"}
                </button>

                <button
                  className="sheet-item"
                  onClick={openContactsVCFPicker}
                  disabled={vcfOpenBusy}
                  title="Open/import a .vcf file from Android, iPhone, or desktop into contacts.db"
                >
                  {vcfOpenBusy ? "⏳ Opening VCF..." : "📂 Open VCF"}
                </button>

                <button className="sheet-item" onClick={exportContactsVCF} disabled={vcfExportBusy}>
                  {vcfExportBusy ? "⏳ Exporting VCF..." : "📇 Export Contacts VCF"}
                </button>

                <button
                  className="sheet-item"
                  onClick={() => go("/chat-seen")}
                  title="Upload WhatsApp chat export, detect phone numbers, match contacts.db, and save chat dates"
                >
                  👁 Chat Seen Contacts
                </button>

                <button
                  className="sheet-item"
                  onClick={() => {
                    closeAll();
                    setBatchOpen(true);
                  }}
                >
                  🧪 CK Batch / Recipe Manager
                </button>

                <button
                  className="sheet-item"
                  onClick={() => {
                    closeAll();
                    setBulkDeleteOpen(true);
                  }}
                >
                  🗑 Bulk Delete
                </button>

                <button
                  className="sheet-item"
                  onClick={() => {
                    closeAll();
                    setFileDlg(true);
                  }}
                >
                  📁 Backend Folder
                </button>

                <button
                  className="sheet-item"
                  onClick={() => {
                    closeAll();
                    setShowPrivateMediaDlg(true);
                  }}
                >
                  🖼 Private Media
                </button>

                <button
                  className="sheet-item"
                  onClick={() => {
                    closeAll();
                    setShowUsersDlg(true);
                  }}
                >
                  👥 Users
                </button>
              </>
            )}
          </div>

          <div className="sheet-sep" />

          <div className="sheet-sec">
            <div className="sheet-title">Navigate</div>
            <button className="sheet-item" onClick={() => go("/")}>🏠 Home</button>
            <button className="sheet-item" onClick={() => go("/search-date")}>📅 Search by Date</button>
            <button className="sheet-item" onClick={() => go("/birthdays")}>🎂 Birthday Search</button>
            <button className="sheet-item" onClick={() => go("/reservations")}>📆 Reservations</button>
            <button className="sheet-item" onClick={() => go("/guest-search")}>🔍 Search Guest</button>
            <PermGate perm="nav_control"><button className="sheet-item" onClick={() => go("/table-db")}>🧾 Table Database</button></PermGate>
            <button className="sheet-item" onClick={openCustomerVisitSearch}>🧾 Customer Visit History</button>
            <button className="sheet-item" onClick={() => go("/control")}>⚙ Control Panel</button>
            <button className="sheet-item" onClick={() => go("/competitions")}>🏆 Competitions</button>
            {isAuthenticated && <button className="sheet-item" onClick={() => { closeAll(); setInstagramAuditOpen(true); }}>📸 Instagram Follow Audit</button>}
          </div>

          {typeof onEndOfDay === "function" && (
            <>
              <div className="sheet-sep" />
              <div className="sheet-sec">
                <div className="sheet-title">End</div>
                <PermGate perm="end_of_day">
                  <button
                    className="sheet-item danger"
                    onClick={() => {
                      closeAll();
                      onEndOfDay();
                    }}
                  >
                    🌙 End of Day
                  </button>
                </PermGate>
              </div>
            </>
          )}

          <div className="sheet-sep" />

          <div className="sheet-sec">
            <div className="sheet-title">Account</div>
            {isAuthenticated ? (
              <button className="sheet-item danger" onClick={handleLogout}>
                🚪 Log out
              </button>
            ) : (
              <button
                className="sheet-item"
                onClick={() => {
                  closeAll();
                  navigate("/login");
                }}
              >
                🔐 Sign in
              </button>
            )}
          </div>
        </div>
      )}

      {visitSearchOpen && (
        <div
          className="dialog-overlay"
          onClick={closeCustomerVisitSearch}
          style={{ zIndex: 2400, padding: 14, alignItems: "center", justifyContent: "center" }}
        >
          <div
            className="dialog-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(980px, 97vw)",
              maxWidth: "97vw",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderRadius: 20,
            }}
          >
            <div
              className="dialog-head"
              style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="dialog-title">🧾 Customer Visit History</div>
                <div style={{ fontSize: 12, opacity: 0.72, marginTop: 3 }}>
                  Search customer name or phone across every permitted branch table.db.
                </div>
              </div>

              <button
                className="btn tiny"
                style={{ marginLeft: "auto" }}
                onClick={closeCustomerVisitSearch}
                disabled={visitLoading}
              >
                Close
              </button>
            </div>

            <form
              onSubmit={runCustomerVisitSearch}
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                padding: "12px 0",
                borderBottom: "1px solid rgba(255,255,255,.08)",
              }}
            >
              <input
                value={visitQuery}
                onChange={(e) => setVisitQuery(e.target.value)}
                placeholder="Customer name or phone number…"
                autoFocus
                disabled={visitLoading}
                style={{
                  flex: "1 1 320px",
                  minWidth: 0,
                  minHeight: 44,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(148,163,184,.28)",
                  background: "rgba(15,23,42,.35)",
                  color: "inherit",
                  fontSize: 15,
                  outline: "none",
                }}
              />

              <button className="btn" type="submit" disabled={visitLoading || String(visitQuery || "").trim().length < 2}>
                {visitLoading ? "⏳ Searching…" : "🔎 Search All Branches"}
              </button>
            </form>

            {visitError && (
              <div
                style={{
                  marginTop: 10,
                  padding: "10px 12px",
                  borderRadius: 12,
                  background: "rgba(239,68,68,.12)",
                  border: "1px solid rgba(239,68,68,.25)",
                  color: "#fecaca",
                  wordBreak: "break-word",
                }}
              >
                ❌ {visitError}
              </div>
            )}

            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingTop: 12 }}>
              {visitLoading ? (
                <div style={{ padding: "28px 8px", textAlign: "center", opacity: 0.8 }}>
                  Searching branch table databases…
                </div>
              ) : visitData ? (
                <div style={{ display: "grid", gap: 12 }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: 8,
                    }}
                  >
                    <div style={{ padding: 10, borderRadius: 12, background: "rgba(255,255,255,.05)" }}>
                      <div style={{ fontSize: 11, opacity: 0.65 }}>Visits found</div>
                      <div style={{ fontSize: 20, fontWeight: 900 }}>{Number(visitData?.count || 0)}</div>
                    </div>
                    <div style={{ padding: 10, borderRadius: 12, background: "rgba(255,255,255,.05)" }}>
                      <div style={{ fontSize: 11, opacity: 0.65 }}>Branches scanned</div>
                      <div style={{ fontSize: 14, fontWeight: 800, marginTop: 3 }}>
                        {(visitData?.scanned_branches || []).map((b) => BRANCH_LABEL[safeLower(b)] || b).join(", ") || "—"}
                      </div>
                    </div>
                    <div style={{ padding: 10, borderRadius: 12, background: "rgba(255,255,255,.05)" }}>
                      <div style={{ fontSize: 11, opacity: 0.65 }}>Database tables checked</div>
                      <div style={{ fontSize: 20, fontWeight: 900 }}>{Number(visitData?.scanned_tables || 0)}</div>
                    </div>
                  </div>

                  {Array.isArray(visitData?.matched_contacts) && visitData.matched_contacts.length > 0 && (
                    <div
                      style={{
                        padding: "9px 11px",
                        borderRadius: 12,
                        background: "rgba(14,165,233,.09)",
                        border: "1px solid rgba(14,165,233,.22)",
                        fontSize: 13,
                      }}
                    >
                      <strong>Contact match:</strong>{" "}
                      {visitData.matched_contacts.slice(0, 4).map((c, i) => (
                        <span key={`${c?.name || "contact"}-${i}`}>
                          {i ? " · " : ""}{c?.name || "Unknown"}{c?.phone ? ` (${c.phone})` : ""}
                        </span>
                      ))}
                    </div>
                  )}

                  {Array.isArray(visitData?.warnings) && visitData.warnings.length > 0 && (
                    <details
                      style={{
                        padding: "9px 11px",
                        borderRadius: 12,
                        background: "rgba(245,158,11,.08)",
                        border: "1px solid rgba(245,158,11,.2)",
                      }}
                    >
                      <summary style={{ cursor: "pointer", fontWeight: 800 }}>
                        ⚠ {visitData.warnings.length} database warning{visitData.warnings.length === 1 ? "" : "s"}
                      </summary>
                      <div style={{ display: "grid", gap: 4, marginTop: 8, fontSize: 12 }}>
                        {visitData.warnings.map((w, i) => <div key={`${w}-${i}`}>{w}</div>)}
                      </div>
                    </details>
                  )}

                  {!Array.isArray(visitData?.items) || visitData.items.length === 0 ? (
                    <div
                      style={{
                        padding: "28px 12px",
                        textAlign: "center",
                        border: "1px dashed rgba(148,163,184,.25)",
                        borderRadius: 14,
                        opacity: 0.78,
                      }}
                    >
                      No matching visit was found in the branch table databases.
                    </div>
                  ) : (
                    <div style={{ display: "grid", gap: 9, paddingBottom: 4 }}>
                      {visitData.items.map((item, index) => {
                        const branch = safeLower(item?.branch || "");
                        const when = [item?.date, item?.time]
                          .map((v) => String(v || "").trim())
                          .filter(Boolean)
                          .join(" · ");

                        return (
                          <div
                            key={`${branch}-${item?.source_table || "table"}-${item?.source_rowid ?? index}-${index}`}
                            style={{
                              padding: 12,
                              borderRadius: 15,
                              border: "1px solid rgba(148,163,184,.16)",
                              background: "rgba(255,255,255,.035)",
                              display: "grid",
                              gap: 10,
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                              <div style={{ fontSize: 16, fontWeight: 900 }}>
                                {item?.customer_name || "Customer"}
                              </div>
                              {item?.phone && (
                                <div style={{ fontSize: 13, opacity: 0.78, wordBreak: "break-all" }}>📱 {item.phone}</div>
                              )}
                              <span
                                style={{
                                  marginLeft: "auto",
                                  padding: "3px 9px",
                                  borderRadius: 999,
                                  fontSize: 12,
                                  fontWeight: 850,
                                  background: "rgba(34,197,94,.11)",
                                  border: "1px solid rgba(34,197,94,.22)",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                📍 {BRANCH_LABEL[branch] || branch || "Unknown branch"}
                              </span>
                            </div>

                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
                                gap: 8,
                              }}
                            >
                              <div>
                                <div style={{ fontSize: 10, opacity: 0.58, textTransform: "uppercase" }}>Table</div>
                                <div style={{ fontWeight: 800 }}>{item?.table || "—"}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: 10, opacity: 0.58, textTransform: "uppercase" }}>Waiter</div>
                                <div style={{ fontWeight: 800 }}>{item?.waiter || "—"}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: 10, opacity: 0.58, textTransform: "uppercase" }}>Date / time</div>
                                <div style={{ fontWeight: 800 }}>{when || item?.datetime || "—"}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: 10, opacity: 0.58, textTransform: "uppercase" }}>Persons</div>
                                <div style={{ fontWeight: 800 }}>{item?.persons || "—"}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: 10, opacity: 0.58, textTransform: "uppercase" }}>Total</div>
                                <div style={{ fontWeight: 800 }}>{item?.total || "—"}</div>
                              </div>
                            </div>

                            <div style={{ fontSize: 10, opacity: 0.5 }}>
                              Source: {item?.source_table || "table.db"}
                              {item?.source_rowid != null ? ` · row ${item.source_rowid}` : ""}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: "24px 8px", opacity: 0.72, textAlign: "center" }}>
                  Enter a customer name or phone number to see where they visited, their table, waiter, and time.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {vcfExportOpen && (
        <div
          className="dialog-overlay"
          onClick={() => {
            if (!vcfExportBusy) resetExportLoading();
          }}
          style={{
            zIndex: 2500,
            padding: 16,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="dialog-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(440px, 96vw)",
              maxWidth: "96vw",
              borderRadius: 22,
              overflow: "hidden",
              boxShadow: "0 35px 100px rgba(0,0,0,.45)",
            }}
          >
            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  aria-hidden="true"
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 16,
                    display: "grid",
                    placeItems: "center",
                    background:
                      vcfExportStatus === "error"
                        ? "rgba(239,68,68,.16)"
                        : vcfExportStatus === "success"
                        ? "rgba(34,197,94,.16)"
                        : "rgba(14,165,233,.16)",
                    border: "1px solid rgba(255,255,255,.12)",
                    fontSize: 24,
                  }}
                >
                  {vcfExportStatus === "error" ? "❌" : vcfExportStatus === "success" ? "✅" : "📇"}
                </div>

                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 900, lineHeight: 1.2 }}>
                    Export Contacts VCF
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.76, marginTop: 3 }}>
                    Works on desktop, Android, iPhone, tablet, and all modern browsers.
                  </div>
                </div>
              </div>

              <div
                style={{
                  fontSize: 14,
                  lineHeight: 1.45,
                  color:
                    vcfExportStatus === "error"
                      ? "#fecaca"
                      : vcfExportStatus === "success"
                      ? "#bbf7d0"
                      : "inherit",
                  wordBreak: "break-word",
                }}
              >
                {vcfExportMessage}
              </div>

              <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={vcfExportPercent}
                style={{
                  height: 12,
                  borderRadius: 999,
                  background: "rgba(148,163,184,.22)",
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,.08)",
                }}
              >
                <div
                  style={{
                    width: `${Math.max(0, Math.min(100, vcfExportPercent))}%`,
                    height: "100%",
                    borderRadius: 999,
                    background:
                      vcfExportStatus === "error"
                        ? "#ef4444"
                        : vcfExportStatus === "success"
                        ? "#22c55e"
                        : "linear-gradient(90deg, #22c55e, #06b6d4)",
                    transition: "width .25s ease",
                  }}
                />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ fontSize: 12, opacity: 0.72 }}>
                  {Math.round(vcfExportPercent)}%
                </div>

                {vcfExportStatus === "error" && (
                  <button className="btn tiny" style={{ marginLeft: "auto" }} onClick={resetExportLoading}>
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <InstagramFollowAudit
        open={instagramAuditOpen}
        onClose={() => setInstagramAuditOpen(false)}
        apiFetch={apiFetchWaitBranch}
        apiBase={BASE}
      />

      <UserAdminModal
        open={showUsersDlg}
        onClose={() => setShowUsersDlg(false)}
        apiFetch={apiFetchWaitBranch}
      />

      <PrivateMediaManager
        open={showPrivateMediaDlg}
        onClose={() => setShowPrivateMediaDlg(false)}
        apiFetch={apiFetchWaitBranch}
        isAdmin={isAdminAuthed}
      />

      {fileDlg && (
        <div className="dialog-overlay" onClick={() => setFileDlg(false)}>
          <div
            className="dialog-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 980,
              width: "95vw",
              height: "80vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="dialog-head" style={{ gap: 8 }}>
              <div className="dialog-title">📁 Backend Folder</div>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <button className="btn" onClick={openBackendFolderOnServer} disabled={openingFolder}>
                  {openingFolder ? "Opening…" : "Open on this PC"}
                </button>
                <a className="btn" href={`${BASE}/backend/browse`} target="_blank" rel="noreferrer">
                  Open in new tab
                </a>
                <button className="btn tiny" onClick={() => setFileDlg(false)}>
                  Close
                </button>
              </div>
            </div>

            <div
              style={{
                flex: 1,
                border: "1px solid rgba(255,255,255,.1)",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <iframe
                title="Backend Browser"
                src={`${BASE}/backend/browse`}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  background: "#0f172a",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {batchOpen && (
        <div className="dialog-overlay" onClick={() => setBatchOpen(false)}>
          <div
            className="dialog-card"
            style={{
              maxWidth: "980px",
              width: "96vw",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dialog-head" style={{ alignItems: "center", gap: 8 }}>
              <div className="dialog-title">🧑‍🍳 CK Batch / Recipe Manager</div>
              <button className="btn tiny" onClick={() => setBatchOpen(false)}>
                Close
              </button>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflow: "auto", marginTop: 8 }}>
              <BatchManager />
            </div>
          </div>
        </div>
      )}

      {bulkDeleteOpen && (
        <BulkDeleteModal
          open={bulkDeleteOpen}
          onClose={() => setBulkDeleteOpen(false)}
          apiBase={BASE}
          authHeaders={getAuthHeaders()}
        />
      )}
    </header>
  );
}
